'use strict'
import { has, get, isEmpty } from 'lodash-es'
import router from '@/router'
import * as API from '@/api'
import { useStorage } from 'vue3-storage'
import { promoteUrl } from '@/utils/url'
import { getHotThreadsAPI } from "@/api";

const actions = {
  getValidParams({ state }, { routerQuery = {}, isUseDefault = false }) {
    // forum 判断优先顺序:
    // 1. 使用传参: routerQuery.forum
    // 2. 使用 state 存取的: state.selectForum
    // 3. 使用预设值: selectedForumDefault
    const item = state.forumSettings.layer2[routerQuery.forum]
    const forum = item
      ? routerQuery.forum
      : state.selectedForum || state.selectedForumDefault
    // page 验证:
    // 1. 将传参转型成正数的数字, 非数字统一改写为 1
    // 2. 如果为 0, 改写为 1
    // 3. 如果 forumSettings 有定义 maxPage, 需小于等于 maxPage
    let page = isNaN(+routerQuery.page) ? 1 : Math.abs(+routerQuery.page)
    if (page === 0) page = 1
    const maxPage = item?.maxPage || page
    if ( +page > +maxPage ) page = maxPage
    // console.log('params before check', routerQuery)
    // console.log('params after check', { forum, page})
    return {
      forum: isUseDefault ? state.selectedForumDefault : forum,
      page: isUseDefault ? 1 : page,
    }
  },
  async navigateForum({ state, commit, dispatch, rootGetters }, { routerQuery = {}, isTriggerRouterUpdateData = false }) {
    // ---------------------------------------------------
    // 有效地路由参数
    const validParams = await dispatch('getValidParams', { routerQuery })
    // ---------------------------------------------------
    // 1. update router and trigger api in router
    if (isTriggerRouterUpdateData) {
      commit('setIsUpdateDataOnRouter', 1)
      await router.push({
        path: '/',
        query: validParams
      })
      return
    // 2. call api here, not in router
    } else {
      const result = await dispatch('getListData', validParams)
      // update router
      commit('setIsUpdateDataOnRouter', -1)
      router.push({
        path: '/',
        query: validParams
      })
      return result
    }
  },
  async getListData({ state, commit, dispatch, rootGetters, rootState }, routerQuery) {
    // 检查登入
    const isRequiredLogin = !!state.forumSettings.layer2[routerQuery.forum]?.requireLogin && !rootState.user.isLogin
    // console.log('checkRequiredLogin', isRequiredLogin)
    if (isRequiredLogin) {
      // 第一次打接口尚未返回
      if (rootState.user.isLogin === null) {
        await dispatch('user/getUserInfo', {}, { root: true })
      }
      if (!rootState.user.isLogin) {
        // window.location.href 的话，会导致登入页的 < 返回会重走这个判断，导致回不去 DU圈
        window.location.replace(promoteUrl() + '/member_vue.php?platform=2&method=login&back=' + encodeURIComponent(document.URL))
        return
      }
    }

    const isSwitchPage = routerQuery.forum !== state.forumListData.target
    const savedListData = isSwitchPage ? [] : state.forumListData.list
    const params = {
      page_size: state.forumListDataDefault.pageSize, // 使用预设值; 未来需要参数化再调整
      page: routerQuery.page,
      option: '',
      // 由路由参数决定 api 传参
      ...state.forumSettings.layer2[routerQuery.forum].apiParams
    }
    // console.log('api params', params)
    commit('setIsListDataLoading', true)
    const response = await API.getForumListAPI(params)
    commit('setIsListDataLoading', false)
    // console.log('api response', response.body)
    // ---------------------------------------------------
    // 验证回传结果
    const returnValidation = {
      hasBody: response?.body,
      // TODO: 大赢家/品牌 pages = 0
      // isValidPage: response?.body?.page_info?.pages == 0 || +response?.body?.page_info?.pages >= +params.page,
      // hasList: response?.body?.list.length > 0,
    }
    const isValidReturns = !(Object.values(returnValidation).some((el) => !el))
    if (isValidReturns) {
      const { list, top_list, page_info } = response.body
      // ---------------------------------------------------
      // update selected tab
      const isValueChange = routerQuery.forum !== state.selectedForum
      if (isValueChange) {
        commit('setSelectedForum', routerQuery.forum)
      }
      // ---------------------------------------------------
      // update list
      const listData = {
        target: routerQuery.forum,
        list: list,
        topList: top_list || [],
        total: +page_info.total,
        pages: +page_info.pages,
        page: +params.page,
        pageSize: +params.page_size,
      }
      // H5 需要拼贴结果
      if (rootGetters.isH5View) {
        const collageList = listData.page === 1 ? listData.list : [ ...savedListData, ...listData.list]
        commit('setForumListData', {
          ...listData,
          list: collageList
        })
      } else {
        commit('setForumListData', listData)
      }
      return {
        isSuccess: true,
        data: response.body
      }
    } else {
      // TODO: 关注需要登入 --> 登入提示
      // 保留: 有回传, 可能是参数有误, 可以重新 query
      // const query = await dispatch('getValidParams', { isUseDefault: true }) // use default params
      // return {
      //   isSuccess: false,
      //   data: response.head,
      //   query
      // }
      commit('setForumListData', state.forumListDataDefault) // use default list data
      return {
        isSuccess: false,
        data: response.head
      }
    }
  },

  // 进入帖子详情页、他人页时, 记录帖子列表状态
  saveForumData({ state, commit }) {
    // save scroll position
    const scrollElmt = document.getElementById('view-scroll-area')
    if (scrollElmt) {
      commit('setForumListData', {
        scrollTop: scrollElmt.scrollTop
      })
    }
    // save to local storage
    const storage = useStorage()
    storage.setStorageSync('forum' + state.selectedForum, state.forumListData)
  },

  async homePageHandler({ state, getters, rootGetters, commit, dispatch }, { to, from }) {
    // ------------------------------------------------------------------- 1 / 6
    const storage = useStorage()
    // [H5 only]如果cache內有帖子列表紀錄
    let cacheData = storage.getStorageSync('forum'+to.query.forum)
    if (has(cacheData, 'list.length') && cacheData.list.length > 0 && rootGetters.isH5View) {
      commit('setSelectedForum', to.query.forum)
      commit('setForumListData', cacheData)
      storage.removeStorageSync('forum'+to.query.forum)
      commit('setIsUpdateDataOnRouter', 0)
      return {}
    }

    const isCheckQuery = state.isUpdateDataOnRouter === 0
    const isCallApi = state.isUpdateDataOnRouter === 1

    if (isCheckQuery) {
      // ------------------------------------------------------------------- 2 / 6
      // 情境 1:
      // 第一次载入页面, 且无 query 时
      // 不添加网址的 query
      if (from.name === undefined && isEmpty(to.query)) {
        const query = await dispatch('getValidParams', { })
        await dispatch('getListData', query)
        return {}
      } else {
        // ------------------------------------------------------------------- 3 / 6
        // 情境 2: not home --> home (ex. 换页, 第一次载入页面)
        //    a. has query --> check value --> update router
        // 情境 3: home --> home ( 同页面切换 )
        //    a. no query --> use from.query or default value --> update router
        //    b. query not same --> check value --> update router
        const routerQuery = isEmpty(to.query) && from.name == 'home' ? from.query : to.query
        const query = await dispatch('getValidParams', { routerQuery })
        // ! h5 无法跳页呈现, 载入页面只能从第一笔开始
        if (rootGetters.isH5View) query.page = 1
        // 因为使用 next 更新 query 的话会呼叫两次 router.beforeEach
        // 为了避免呼叫两次接口
        // 所以第一次先检查并更新 query，第二次时再呼叫接口
        commit('setIsUpdateDataOnRouter', 1)
        return {
          name: 'home',
          query: query
        }
      }
    }

    if (isCallApi) {
      const result = await dispatch('getListData', to.query)
      // ------------------------------------------------------------------- 4 / 6
      // fail --> recall ( result.query: 有回传代表可能是参数有误, 用预设值重新 query )
      if (!result.isSuccess && result.query && getters.isRouterFailRecallToMaxTimes) {
        // 计数 recall 次数 ( avoid recall loop )
        commit('setRouterFailRecallCount')
        return {
          name: 'home',
          query: result.query
        }
      } else {
        // ------------------------------------------------------------------- 5 / 6
        commit('setRouterFailRecallCount', 0)
        commit('setIsUpdateDataOnRouter', 0)
        return {}
      }
    }

    // ------------------------------------------------------------------- 6 / 6
    return {}
  }
}

export default actions
